import React, { useEffect, useState, useRef, useCallback } from 'react'
import classNames from 'classnames'
import { Icon, Range } from '../../../../../atoms'

interface CalculatorInputProps {
  id: string
  name: string
  type: string
  value?: number
  placeholder?: number
  onChange: Function
  showDot?: boolean
  dotColor?: string
  resetButton?: boolean
  readOnly?: boolean
  prefix?: string
  suffix?: string
  format?: string
  error?: boolean
  enableRange?: boolean
  min?: number
  max?: number
  step?: number
}

export const CalculatorInput = ({
  id,
  name,
  type,
  value,
  placeholder,
  onChange,
  showDot,
  dotColor,
  prefix,
  resetButton,
  readOnly,
  suffix,
  format,
  enableRange,
  min,
  max,
  step,
  error = false,
}: CalculatorInputProps) => {
  const formatString = (value: string) => {
    if (type === 'text' && format !== 'thousands') {
      return value
    }

    if (type === 'percent') {
      return value
    }

    const s = value.replace(/[^0-9]/g, '')
    if (type === 'text' && format === 'thousands') {
      return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    if (type === 'years') {
      return s
    }

    return s
  }

  const toInteger = (v: string) => {
    if (type === 'percent') {
      return Number(v)
    }
    const parsed = parseInt(v.replace(/[^0-9]/g, ''))
    return isNaN(parsed) ? 0 : parsed
  }

  const toString = useCallback(
    (v: number) => {
      const s = v.toString()

      if (type === 'text' && format === 'thousands') {
        return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }

      if (type === 'years') {
        return s
      }

      return s
    },
    [format, type],
  )

  const [valueString, setValueString] = useState(value ? toString(value) : '')

  const initialValue = value ? toString(value) : ''
  useEffect(() => {
    value && setValueString(toString(value))
  }, [value, toString])

  const rangeRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (rangeRef && rangeRef.current) {
      rangeRef.current.value = `${value}`
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeRef])

  const handleChange = (e: any) => {
    setValueString(formatString(e.target.value))
    onChange({
      ...e,
      target: {
        ...e.target,
        value: toInteger(e.target.value),
      },
    })

    if (rangeRef && rangeRef.current) {
      rangeRef.current.value = type === 'percent' ? e.target.value : `${toInteger(e.target.value)}`
    }
  }

  return (
    <div className='Calculator-input-components-wrapper'>
      <div className='Calculator-input-wrapper'>
        {showDot && (
          <span style={{ background: dotColor }} className={'Calculator-input-dot'}></span>
        )}
        {prefix && (
          <span
            className={classNames('Calculator-input-prefix', {
              'Calculator-input-dot-prefix': showDot,
            })}
          >
            {prefix}
          </span>
        )}
        <input
          id={id}
          name={name}
          placeholder={placeholder?.toString()}
          data-format={format}
          data-calculator-value={valueString}
          type={type}
          onChange={handleChange}
          value={valueString}
          className={classNames('Calculator-input', {
            'Calculator-input-prefix-space': prefix,
            'Calculator-input-suffix-space': suffix,
            'Calculator-input__error': error,
            'Calculator-input-dot-prefix-space': showDot && prefix,
          })}
          readOnly={readOnly}
        />
        {suffix && <span className='Calculator-input-suffix'>{suffix}</span>}
        {resetButton && (
          <button
            type='button'
            aria-label={`reset ${name}`}
            className='Calculator-input-reset-button'
            onClick={() => handleChange({ target: { value: initialValue } })}
          >
            <Icon glyph={'arrow-counter-clockwise'} size={13} />
          </button>
        )}
      </div>
      {enableRange && (
        <div className='Calculator-input-range'>
          <Range
            ref={rangeRef}
            min={min ?? 0}
            max={max ?? 0}
            step={step ?? 0}
            onChange={handleChange}
          />
        </div>
      )}
    </div>
  )
}

export default CalculatorInput
